import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, Panel } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ClipboardList, Search, FileBox, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const nf = (n) => Number(n || 0).toLocaleString("id-ID");
const PRIO = { "Urgent": "bg-red-100 text-red-700", "Prepare Now": "bg-amber-100 text-amber-700" };
const STAT = { "Baru": "bg-blue-100 text-blue-700", "Draft Dibuat": "bg-emerald-100 text-emerald-700", "Ditolak": "bg-red-100 text-red-700", "Dibatalkan": "bg-gray-100 text-gray-500" };

export default function RestockProposals() {
  const { canEdit } = useAuth();
  const canProd = canEdit("prod_production");
  const canReq = canEdit("stock_prediction");
  const [data, setData] = useState({ rows: [], counts: {} });
  const [statusF, setStatusF] = useState("Baru");
  const [q, setQ] = useState("");
  const [convert, setConvert] = useState(null);

  const load = () => api.get("/restock/proposals", { params: { status: statusF, q: q || undefined } })
    .then((r) => setData(r.data)).catch(() => toast.error("Gagal memuat usulan"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF]);

  const reject = async (p) => {
    const reason = window.prompt(`Alasan menolak usulan ${p.number}?`, "");
    if (reason === null) return;
    try { await api.post(`/restock/proposals/${p.id}/reject`, { reason }); toast.success("Usulan ditolak"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async (p) => {
    if (!window.confirm(`Batalkan usulan ${p.number}?`)) return;
    try { await api.delete(`/restock/proposals/${p.id}`); toast.success("Usulan dibatalkan"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const importExisting = async () => {
    if (!window.confirm("Impor Material PR restock lama ke daftar Usulan Restock? Aman & bisa diulang.")) return;
    try { const r = await api.post("/restock/proposals/import-existing"); toast.success(`${r.data.created} usulan diimpor`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const c = data.counts || {};
  return (
    <div data-testid="restock-proposals-page">
      <PageHeader title="Usulan Restock" subtitle="Usulan dari tim sales — produksi meninjau lalu mengirim ke Draft Production Planning.">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-wmuted absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cari produk / varian / no…" className="pl-8 w-56 bg-white" data-testid="rp-search" />
          </div>
          {canProd && <Button variant="outline" onClick={importExisting} className="border-cocoa text-cocoa" data-testid="rp-import-btn"><FileBox className="w-4 h-4 mr-1.5" />Impor dari Material PR</Button>}
        </div>
      </PageHeader>

      <div className="flex gap-2 mb-3 flex-wrap">
        {["Baru", "Draft Dibuat", "Ditolak", "Dibatalkan", "all"].map((s) => (
          <button key={s} onClick={() => setStatusF(s)} data-testid={`rp-tab-${s}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${statusF === s ? "bg-cocoa text-ivory border-cocoa" : "bg-white text-cocoa border-taupe/50 hover:bg-cocoa/5"}`}>
            {s === "all" ? "Semua" : s}{s !== "all" && c[s] != null ? ` (${c[s]})` : ""}
          </button>
        ))}
      </div>

      <Panel className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>No</TableHead><TableHead>Produk / Varian</TableHead>
              <TableHead className="text-right">Terjual 30h</TableHead><TableHead className="text-right">Available</TableHead>
              <TableHead className="text-right">Days Cover</TableHead><TableHead className="text-right">Qty Usulan</TableHead>
              <TableHead>Prioritas</TableHead><TableHead>Status</TableHead><TableHead>Diminta</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.length === 0
              ? <TableRow><TableCell colSpan={10} className="text-center text-wmuted py-10">Tidak ada usulan.</TableCell></TableRow>
              : data.rows.map((p) => (
                <TableRow key={p.id} data-testid={`rp-row-${p.id}`}>
                  <TableCell className="font-mono text-xs">{p.number}</TableCell>
                  <TableCell><div className="font-medium text-cocoa">{p.product_name}</div><div className="text-xs text-wmuted">{p.color || "-"} / {p.size || "-"}</div></TableCell>
                  <TableCell className="text-right">{nf(p.sold_30d)}</TableCell>
                  <TableCell className="text-right">{nf(p.available)}</TableCell>
                  <TableCell className="text-right">{p.days_cover != null ? `${Number(p.days_cover).toFixed(1)} hari` : "-"}</TableCell>
                  <TableCell className="text-right font-semibold">{nf(p.requested_qty)}</TableCell>
                  <TableCell><Badge className={`border-0 ${PRIO[p.priority] || "bg-gray-100 text-gray-600"}`}>{p.priority || "-"}</Badge></TableCell>
                  <TableCell>
                    <Badge className={`border-0 ${STAT[p.status] || ""}`}>{p.status}</Badge>
                    {(p.plan_number || p.material_pr_number) && <div className="text-[11px] text-wmuted mt-0.5">{p.plan_number || p.material_pr_number}</div>}
                    {p.status === "Ditolak" && p.reject_reason && <div className="text-[11px] text-red-600 mt-0.5">{p.reject_reason}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-wmuted">{p.requested_by || "-"}</TableCell>
                  <TableCell className="text-right">
                    {p.status === "Baru" && canProd && <Button size="sm" onClick={() => setConvert({ ...p, qty: p.requested_qty })} className="bg-cocoa hover:bg-cocoa-dark text-ivory mr-1" data-testid={`rp-tomat-${p.id}`}><FileBox className="w-3.5 h-3.5 mr-1" />Kirim ke Planning</Button>}
                    {p.status === "Baru" && canProd && <Button size="icon" variant="ghost" title="Tolak" onClick={() => reject(p)} data-testid={`rp-reject-${p.id}`}><XCircle className="w-4 h-4 text-status-rejected" /></Button>}
                    {p.status === "Baru" && canReq && !canProd && <Button size="icon" variant="ghost" title="Batalkan" onClick={() => cancel(p)} data-testid={`rp-cancel-${p.id}`}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Panel>

      {convert && <ConvertDialog p={convert} onClose={() => setConvert(null)} onDone={() => { setConvert(null); load(); }} />}
    </div>
  );
}

function ConvertDialog({ p, onClose, onDone }) {
  const [qty, setQty] = useState(p.qty);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const n = Number(qty);
    if (!Number.isInteger(n) || n <= 0) { toast.error("Qty harus bilangan bulat > 0"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/restock/proposals/${p.id}/to-plan`, { qty: n });
      toast.success(r.data.created ? `Draft Planning ${r.data.plan_number} dibuat` : `Ditambahkan ke Draft Planning ${r.data.plan_number}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="rp-convert-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Kirim ke Draft Planning</DialogTitle></DialogHeader>
        <div className="text-sm text-cocoa">{p.product_name} · {p.color || "-"}/{p.size || "-"}</div>
        <div className="text-xs text-wmuted mb-2">Usulan {p.number} · Qty usulan {nf(p.requested_qty)} pcs</div>
        <label className="text-sm text-wmuted">Qty diproduksi (pcs)</label>
        <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="bg-white mt-1" data-testid="rp-convert-qty" />
        <p className="text-[11px] text-wmuted mt-2">Ditambahkan sebagai target di Draft Production Plan produk ini (dibuat baru bila belum ada), lalu lanjut di wizard Planning.</p>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={submit} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="rp-convert-save">{saving ? "Memproses…" : "Kirim ke Planning"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
